<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="kpi-threshold-container">
    <div>
      <input
        :value="warningInput"
        :dark="$vuetify.theme.dark"
        class="kpi-input kpi-input-warning"
        @change="onWarningUpdate($event.target.value)"
      />
    </div>
    <div>
      <input
        :value="dangerInput"
        :dark="$vuetify.theme.dark"
        class="kpi-input kpi-input-danger"
        @change="onDangerUpdate($event.target.value)"
      />
    </div>
    <div class="kpi-description">
      <v-tooltip v-if="!isValid" z-index="151" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon class="kpi-validity-icon" :color="descriptionErrorColor" v-bind="attrs" v-on="on">
            {{ descriptionErrorIcon }}
          </v-icon>
        </template>
        <span>{{ descriptionErrorLabel }}</span>
      </v-tooltip>
      {{ `${$t(`kpis.${id}`)} ${units}` }}
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'EcsBusinessProcessKpiThresholdsInput',

  props: {
    id: {
      type: String,
      required: true
    },
    warning: {
      type: [String, Number],
      default: null
    },
    danger: {
      type: [String, Number],
      default: null
    },
    type: {
      type: String,
      required: true,
      validator: value => {
        return ['TimeSpan', 'Percentage', 'Count'].includes(value);
      }
    }
  },

  data() {
    return {
      warningInput: this.warning,
      dangerInput: this.danger,
      validity: { errors: [], warnings: [] }
    };
  },

  computed: {
    warningAmount() {
      return this.getAmount(this.warningInput);
    },

    dangerAmount() {
      return this.getAmount(this.dangerInput);
    },

    isEmpty() {
      return !this.warningInput && !this.dangerInput;
    },

    hasErrors() {
      return this.validity.errors.length;
    },

    hasWarnings() {
      return this.validity.warnings.length;
    },

    isValid() {
      return !this.hasErrors && !this.hasWarnings;
    },

    units() {
      return this.type === 'Percentage' ? '%' : '';
    },

    descriptionErrorLabel() {
      return this.hasErrors ? this.validity.errors[0] : this.validity.warnings[0];
    },

    descriptionErrorIcon() {
      return this.hasErrors ? 'mdi-alert-circle' : 'mdi-alert';
    },

    descriptionErrorColor() {
      return this.hasErrors ? 'var(--v-error-base)' : 'var(--v-warning-base)';
    }
  },

  mounted() {
    this.checkValidity();
  },

  methods: {
    isValidAmout(amount) {
      if (!amount) {
        return false;
      }

      if (this.type === 'TimeSpan') {
        return moment(amount).isValid();
      } else if (this.type === 'Percentage') {
        return !Number.isNaN(amount) && amount >= 0 && amount <= 100;
      } else {
        return !Number.isNaN(amount) && amount >= 0;
      }
    },

    getAmount(input) {
      if (!input) {
        return null;
      }

      if (this.type === 'TimeSpan') {
        return moment(input, 'm:s.S') || moment(input, 'H:m:s.S');
      } else {
        return Number.parseFloat(input);
      }
    },

    validateKipThresholds(isValidWarning, isValidDanger, validity) {
      if (this.warningInput && this.dangerInput && isValidWarning && isValidDanger) {
        const isValid =
          this.id === 'businessProcessServiceLevel'
            ? this.dangerAmount < this.warningAmount
            : this.warningAmount < this.dangerAmount;
        if (!isValid) {
          validity.errors.push(this.$t('errors.invalidKpiThresholds'));
        }
      }
    },

    checkValidity() {
      const validity = { errors: [], warnings: [] };

      if (!this.isEmpty) {
        const isValidWarning = !this.warningInput || this.isValidAmout(this.warningAmount);
        const isValidDanger = !this.dangerInput || this.isValidAmout(this.dangerAmount);

        if (!isValidWarning) {
          validity.errors.push(this.$t('errors.invalidWarningValue'));
        }
        if (!isValidDanger) {
          validity.errors.push(this.$t('errors.invalidDangerValue'));
        }

        this.validateKipThresholds(isValidWarning, isValidDanger, validity);

        if (!this.warningAmount || !this.dangerAmount) {
          validity.warnings.push(this.$t('warnings.unableToCalculateKpiStatus'));
        }
      }

      this.validity = validity;
    },

    onWarningUpdate(value) {
      this.warningInput = value.trim();
      this.checkValidity();
      this.$emit('change', {
        id: this.id,
        warning: this.type === 'TimeSpan' ? this.warningAmount.format('HH:mm:ss') : this.warningAmount,
        danger: this.type === 'TimeSpan' ? this.dangerAmount.format('HH:mm:ss') : this.dangerAmount,
        isValid: this.isValid
      });
    },

    onDangerUpdate(value) {
      this.dangerInput = value.trim();
      this.checkValidity();
      this.$emit('change', {
        id: this.id,
        warning: this.type === 'TimeSpan' ? this.warningAmount.format('HH:mm:ss') : this.warningAmount,
        danger: this.type === 'TimeSpan' ? this.dangerAmount.format('HH:mm:ss') : this.dangerAmount,
        isValid: this.isValid
      });
    },

    reset() {
      this.warningInput = this.warning;
      this.dangerInput = this.danger;
      this.checkValidity();
    }
  }
};
</script>

<style scoped>
.kpi-threshold-container {
  display: inline-grid;
  grid-template-columns: 26px 26px auto;
  grid-gap: 4px;
  width: 100%;
}

.kpi-input {
  width: 100%;
  text-align: center;
  color: #000000;
}

.kpi-input:focus {
  outline: unset;
}

[dark].kpi-input {
  color: #ffffff;
}

.kpi-input-warning {
  border-bottom: 1px solid var(--v-warning-base);
}

.kpi-input-danger {
  border-bottom: 1px solid indianred;
}

.kpi-validity-icon {
  font-size: 12px;
  margin-top: -1px;
}

.kpi-description {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>
